import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConfigState {
  vigileId: string;
  eventId: string;
  eventDate: string;
  deviceId: string;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  autoSync: boolean;
  isConfigured: boolean;

  setConfig: (partial: Partial<Pick<ConfigState,
    | 'vigileId'
    | 'eventId'
    | 'eventDate'
    | 'soundEnabled'
    | 'vibrationEnabled'
    | 'autoSync'
  >>) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateDeviceId(): string {
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `PWA-${rand}`;
}

function computeConfigured(state: { eventId: string; eventDate: string }): boolean {
  return state.eventId !== '' && state.eventDate !== '';
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULTS = {
  vigileId: '',
  eventId: '',
  eventDate: '',
  deviceId: '',
  soundEnabled: true,
  vibrationEnabled: true,
  autoSync: true,
  isConfigured: false,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useConfigStore = create<ConfigState>()(
  persist(
    (set) => ({
      ...DEFAULTS,

      setConfig: (partial) =>
        set((state) => {
          const next = { ...state, ...partial };
          return {
            ...partial,
            isConfigured: computeConfigured(next),
          };
        }),

      reset: () => set(DEFAULTS),
    }),
    {
      name: 'control-billet-config',
      partialize: (state) => ({
        vigileId: state.vigileId,
        eventId: state.eventId,
        eventDate: state.eventDate,
        deviceId: state.deviceId,
        soundEnabled: state.soundEnabled,
        vibrationEnabled: state.vibrationEnabled,
        autoSync: state.autoSync,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Ensure deviceId is always set.
          if (!state.deviceId) {
            state.deviceId = generateDeviceId();
          }
          state.isConfigured = computeConfigured(state);
        }
      },
    },
  ),
);
